import type { TFunction } from 'i18next';

import { getActivityDependencyFeedbackKey } from 'src/utils/activity-dependency-errors';

export type ScheduleApiError = {
  status?: number;
  errorCode?: string;
  message?: string;
  key?: string;
};

export type ScheduleOperationFeedback =
  | { kind: 'translated'; key: string; values?: Record<string, unknown> }
  | { kind: 'generic' };

export const SCHEDULE_ERROR_CODE = {
  ALREADY_ALLOCATED: 'CFE000019',
  BLACKOUT_DATE: 'CFE000006',
  EXCLUDED_DATE: 'CFE000029',
  BUSINESS_UNIT: 'CFE000005',
  PROJECT_ROLE: 'CFE000014',
  ALLOCATED_AUDITOR: 'CFE000045',
  ACTIVITY_SCHEDULE_MODIFICATION_BLOCKED: 'CFE000052',
  ACTIVITY_STATUS_SCHEDULE_MODIFICATION_BLOCKED: 'CFE000053',
  BOOKING_REQUEST_IN_PROGRESS: 'CFE000046',
} as const;

const MESSAGE_KEY_BY_CODE: Record<string, string> = {
  [SCHEDULE_ERROR_CODE.ALREADY_ALLOCATED]: 'ACTIVITY_ALREADY_ALLOCATED',
  [SCHEDULE_ERROR_CODE.BLACKOUT_DATE]: 'BLACKOUT_DATE_VIOLATED',
  [SCHEDULE_ERROR_CODE.EXCLUDED_DATE]: 'ACTIVITY_EXCLUDED_DATE_VIOLATED',
  [SCHEDULE_ERROR_CODE.BUSINESS_UNIT]: 'BUSINESS_UNIT_VIOLATED',
  [SCHEDULE_ERROR_CODE.PROJECT_ROLE]: 'PROJECT_ROLE_REQUIRED',
  [SCHEDULE_ERROR_CODE.ALLOCATED_AUDITOR]: 'ALLOCATED_AUDITOR_REQUIRED',
  [SCHEDULE_ERROR_CODE.ACTIVITY_SCHEDULE_MODIFICATION_BLOCKED]:
    'ACTIVITY_SCHEDULE_MODIFICATION_BLOCKED',
  [SCHEDULE_ERROR_CODE.ACTIVITY_STATUS_SCHEDULE_MODIFICATION_BLOCKED]:
    'ACTIVITY_STATUS_SCHEDULE_MODIFICATION_BLOCKED',
  [SCHEDULE_ERROR_CODE.BOOKING_REQUEST_IN_PROGRESS]: 'BOOKING_REQUEST_IN_PROGRESS',
};

const CODE_BY_MESSAGE_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(MESSAGE_KEY_BY_CODE).map(([code, key]) => [key, code])
);

function readErrorBody(data: unknown): Pick<ScheduleApiError, 'errorCode' | 'message' | 'key'> {
  if (!data || typeof data !== 'object') {
    return {};
  }
  const body = data as Record<string, unknown>;
  const rawMessage = body.message;
  let message: string | undefined;
  if (typeof rawMessage === 'string') {
    message = rawMessage;
  } else if (Array.isArray(rawMessage)) {
    message = rawMessage.filter((part): part is string => typeof part === 'string').join(', ');
  }
  return {
    errorCode: typeof body.errorCode === 'string' ? body.errorCode : undefined,
    message,
    key: typeof body.key === 'string' ? body.key : undefined,
  };
}

/** Normalizes axios failures and plain API error payloads for schedule toasts. */
export function toScheduleApiError(error: unknown): ScheduleApiError {
  if (!error || typeof error !== 'object') {
    return {};
  }

  const candidate = error as Record<string, unknown>;

  const response = candidate.response;
  if (response && typeof response === 'object') {
    const responseRecord = response as Record<string, unknown>;
    const status =
      typeof responseRecord.status === 'number' ? responseRecord.status : undefined;
    return { status, ...readErrorBody(responseRecord.data) };
  }

  const fromBody = readErrorBody(candidate);
  return {
    status: typeof candidate.status === 'number' ? candidate.status : undefined,
    errorCode:
      typeof candidate.errorCode === 'string' ? candidate.errorCode : fromBody.errorCode,
    message: typeof candidate.message === 'string' ? candidate.message : fromBody.message,
    key: typeof candidate.key === 'string' ? candidate.key : fromBody.key,
  };
}

function resolveAllowlistedCode(error: ScheduleApiError): string | undefined {
  if (error.errorCode && MESSAGE_KEY_BY_CODE[error.errorCode]) {
    return error.errorCode;
  }
  if (error.message && CODE_BY_MESSAGE_KEY[error.message]) {
    return CODE_BY_MESSAGE_KEY[error.message];
  }
  if (error.key && CODE_BY_MESSAGE_KEY[error.key]) {
    return CODE_BY_MESSAGE_KEY[error.key];
  }
  return undefined;
}

export function mapScheduleOperationError(
  error: unknown,
  options?: { visitId?: number; includeProjectRole?: boolean }
): ScheduleOperationFeedback {
  const apiError = toScheduleApiError(error);
  const code = resolveAllowlistedCode(apiError);

  if (code === SCHEDULE_ERROR_CODE.ALREADY_ALLOCATED) {
    return {
      kind: 'translated',
      key: 'alreadyAllocated',
      values: options?.visitId != null ? { visitId: options.visitId } : undefined,
    };
  }
  if (code === SCHEDULE_ERROR_CODE.BLACKOUT_DATE) {
    return { kind: 'translated', key: 'feedbackCodes.BLACKOUT_DATE_VIOLATED' };
  }
  if (code === SCHEDULE_ERROR_CODE.EXCLUDED_DATE) {
    return { kind: 'translated', key: 'feedbackCodes.ACTIVITY_EXCLUDED_DATE_VIOLATED' };
  }
  if (code === SCHEDULE_ERROR_CODE.BUSINESS_UNIT) {
    return { kind: 'translated', key: 'feedbackCodes.BUSINESS_UNIT_VIOLATED' };
  }
  if (options?.includeProjectRole && code === SCHEDULE_ERROR_CODE.PROJECT_ROLE) {
    return { kind: 'translated', key: 'common:feedbackCodes.PROJECT_ROLE_REQUIRED' };
  }
  if (options?.includeProjectRole && code === SCHEDULE_ERROR_CODE.ALLOCATED_AUDITOR) {
    return { kind: 'translated', key: 'common:feedbackCodes.ALLOCATED_AUDITOR_REQUIRED' };
  }
  if (code === SCHEDULE_ERROR_CODE.ACTIVITY_SCHEDULE_MODIFICATION_BLOCKED) {
    return {
      kind: 'translated',
      key: 'common:feedbackCodes.ACTIVITY_SCHEDULE_MODIFICATION_BLOCKED',
    };
  }
  if (code === SCHEDULE_ERROR_CODE.ACTIVITY_STATUS_SCHEDULE_MODIFICATION_BLOCKED) {
    return {
      kind: 'translated',
      key: 'common:feedbackCodes.ACTIVITY_STATUS_SCHEDULE_MODIFICATION_BLOCKED',
    };
  }
  if (code === SCHEDULE_ERROR_CODE.BOOKING_REQUEST_IN_PROGRESS) {
    return {
      kind: 'translated',
      key: 'common:feedbackCodes.BOOKING_REQUEST_IN_PROGRESS',
    };
  }

  const dependencyKey = getActivityDependencyFeedbackKey(error);
  if (dependencyKey) {
    return { kind: 'translated', key: `common:feedbackCodes.${dependencyKey}` };
  }

  return { kind: 'generic' };
}

/** Toast text for schedule API failures (maps business errors before the generic key). */
export function formatScheduleOperationErrorMessage(
  error: unknown,
  t: TFunction,
  genericKey: string,
  options?: { visitId?: number; includeProjectRole?: boolean }
): string {
  const feedback = mapScheduleOperationError(error, options);
  if (feedback.kind === 'translated') {
    return t(feedback.key, feedback.values);
  }
  return t(genericKey);
}

export function isRetryablePlanBoxError(error: unknown): boolean {
  return isRetryableTransientScheduleError(error);
}

/** Network failures (no status) and 5xx responses are safe to retry once. */
export function isRetryableTransientScheduleError(error: unknown): boolean {
  const { status } = toScheduleApiError(error);
  if (status == null) {
    return true;
  }
  return status >= 500 && status < 600;
}

export function isPlanBoxDeleteAlreadyGone(error: unknown): boolean {
  return toScheduleApiError(error).status === 404;
}
